<script setup lang="ts">
import GemeindeSelect from '@/components/gemeinde-by-ort-select.vue'
import ApplyDlg from '@/components/liegenschaft-apply-dlg.vue'
import KpTextfield from '@/components/kp-textfield.vue'
import { computed, ref, type Ref } from 'vue'
import { isAdmin, useAuthUser } from '@/helpers/globals'
import type { Liegenschaft } from '@/models/Liegenschaft'
import { useI18n } from 'vue-i18n'
import { useToast } from '@/composables/toast'
import { formRules } from '@/helpers/validators'
import type { ObjektRahmen } from '@/models/apollon_conf_fk/ObjektRahmen'
import { completeStreetString, errToStr } from '@/helpers/tools'
import KpNumberfield from '../kp-numberfield.vue'
import KpEgidfield from '../kp-egidfield.vue'
import PlzOrtSelect from '../plz-ort-select.vue'

export type GwrData = {
    key: string
    gwrValue?: string | number | null
    value: string | number | null | undefined
}
const {
    liegenschaft,
    readOnly = false,
    felderConfig,
} = defineProps<{
    liegenschaft: Liegenschaft | null
    readOnly?: boolean
    felderConfig?: ObjektRahmen
}>()
const rules = formRules
const gwrdata: Ref<GwrData[]> = ref([])
const applyDlg = ref(false)
const egidLoading = ref(false)
const showEgidTooltip = ref(false)

const { t } = useI18n()
const toast = useToast()
const benutzer = useAuthUser()

const modStreet = (value: string | null) => completeStreetString(value)

const addressComplete = computed(() => {
    return (
        liegenschaft?.ort_id && liegenschaft?.ort_id > 0 && (liegenschaft?.strasse || '').length > 0
    )
})
const readOnlyCalc = computed(() => {
    if (isAdmin()) {
        return false
    }
    if (liegenschaft?.ersteller_id) {
        return readOnly || liegenschaft?.ersteller_id !== benutzer?.id
    }
    return readOnly
})

async function egidLookup() {
    showEgidTooltip.value = false
    if (!liegenschaft?.ort_id || !liegenschaft?.gemeinde_id || !liegenschaft?.strasse) {
        toast.add({ text: t('address_please'), color: 'error' })
        return
    }
    try {
        egidLoading.value = true
        const egid = await liegenschaft?.egidQuery()
        if (egid) {
            liegenschaft.set('egid', Number.parseInt(egid.egid, 10))
            liegenschaft.set('coord_x', egid.x)
            liegenschaft.set('coord_y', egid.y)
            gwrdata.value = []
            if (showGWRValue(egid.strasse, liegenschaft?.strasse)) {
                gwrdata.value.push({
                    key: 'strasse',
                    gwrValue: egid.strasse,
                    value: liegenschaft?.strasse,
                })
            } else {
                liegenschaft.set('strasse', egid.strasse)
            }
            if (showGWRValue(egid.strassen_nummer, liegenschaft?.strassen_nummer)) {
                gwrdata.value.push({
                    key: 'strassen_nummer',
                    gwrValue: egid.strassen_nummer,
                    value: liegenschaft?.strassen_nummer,
                })
            } else {
                liegenschaft.set('strassen_nummer', egid.strassen_nummer)
            }
            if (showGWRValue(Number.parseInt(egid.baujahr, 10), liegenschaft?.baujahr)) {
                gwrdata.value.push({
                    key: 'baujahr',
                    gwrValue: Number.parseInt(egid.baujahr, 10),
                    value: liegenschaft?.baujahr,
                })
            } else {
                liegenschaft.set('baujahr', Number.parseInt(egid.baujahr, 10))
            }
            if (showGWRValue(egid.parzelleNr, liegenschaft?.parzellen_nummer)) {
                gwrdata.value.push({
                    key: 'parzellen_nummer',
                    gwrValue: egid.parzelleNr,
                    value: liegenschaft?.parzellen_nummer,
                })
            } else {
                liegenschaft.set('parzellen_nummer', egid.parzelleNr)
            }
            if (showGWRValue(egid.assekuranz_nummer, liegenschaft?.assekuranz_nummer)) {
                gwrdata.value.push({
                    key: 'assekuranz_nummer',
                    gwrValue: egid.assekuranz_nummer,
                    value: liegenschaft?.assekuranz_nummer,
                })
            } else {
                liegenschaft.set('assekuranz_nummer', egid.assekuranz_nummer)
            }
            applyDlg.value = gwrdata.value.length > 0
            if (gwrdata.value.length === 0) {
                toast.add({ text: t('check_gwr_data'), color: 'warning', timeout: 10000 })
            }
        } else {
            liegenschaft.set('egid', null)
            toast.add({ text: t('no_egid_found'), color: 'warning', timeout: 0 })
        }
    } catch (e) {
        liegenschaft.set('egid', null)
        toast.add({ text: errToStr(e), color: 'error' })
    } finally {
        egidLoading.value = false
    }
}
function showGWRValue(gwrValue: string | number | null, actualValue: string | number | null) {
    return actualValue && actualValue !== gwrValue
}

function onOrtIdChange() {
    clearEgid()
    // clear Gemeinde-ID, User must select again
    liegenschaft?.set('gemeinde_id', null)
}

function applyGwrData(data: GwrData[]) {
    applyDlg.value = false
    data.forEach((d) => liegenschaft?.set(d.key, d.value))
}

function infotext(fieldIdent: string): string | null {
    if (felderConfig && felderConfig.felder && felderConfig.felder[fieldIdent]) {
        return felderConfig.felder[fieldIdent].hilfetext || null
    }
    return null
}

function clearEgid() {
    liegenschaft?.set('egid', null)
    liegenschaft?.set('baujahr', null)
    liegenschaft?.set('parzellen_nummer', null)
    liegenschaft?.set('assekuranz_nummer', null)
    liegenschaft?.set('coord_x', null)
    liegenschaft?.set('coord_y', null)
}
</script>

<template>
    <v-container v-if="liegenschaft" fluid>
        <v-row>
            <v-col sm="8" cols="8">
                <kp-textfield
                    :model-value="liegenschaft.strasse"
                    @update:model-value="(value) => liegenschaft.set('strasse', value)"
                    @blur="() => liegenschaft.set('strasse', modStreet(liegenschaft.strasse))"
                    dense
                    autocomplete="new-password"
                    :label="$t('strasse') + ' *'"
                    :readonly="readOnlyCalc"
                    :rules="[rules.required, rules.maxLength(100), rules.charsOnly]"
                    @change="clearEgid"
                />
            </v-col>
            <v-col sm="4" cols="4">
                <kp-textfield
                    :model-value="liegenschaft.strassen_nummer"
                    @update:model-value="(value) => liegenschaft.set('strassen_nummer', value)"
                    dense
                    autocomplete="new-password"
                    :label="$t('nr')"
                    :readonly="readOnlyCalc"
                    :rules="[rules.maxLength(20), rules.houseNumber]"
                    @change="clearEgid"
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" sm="6">
                <PlzOrtSelect
                    :ort-id="liegenschaft.ort_id"
                    @update:ort-id="
                        (value: number | null | undefined) => {
                            liegenschaft.set('ort_id', value)
                            onOrtIdChange()
                        }
                    "
                    dense
                    :read-only="readOnlyCalc"
                    :rules="[rules.required]"
                />
            </v-col>
            <v-col cols="12" sm="6">
                <GemeindeSelect
                    :model-value="liegenschaft.gemeinde_id"
                    @update:model-value="
                        (value: number | null | undefined) => liegenschaft.set('gemeinde_id', value)
                    "
                    dense
                    :ort-id="liegenschaft.ort_id"
                    :label="$t('gemeinde') + ' *'"
                    :read-only="readOnlyCalc"
                    :rules="[rules.required]"
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" sm="6">
                <KpEgidfield
                    :model-value="liegenschaft.egid"
                    @update:model-value="(value) => liegenschaft.set('egid', value)"
                    :readonly="true"
                    :clearable="!readOnly"
                    clear-icon="fas fa-times"
                    :label="$t('egid')"
                    :loading="egidLoading"
                    :infotext="infotext('egid')"
                >
                    <template #append>
                        <v-tooltip v-model="showEgidTooltip" bottom>
                            <template v-slot:activator="{ props }">
                                <v-btn
                                    color="primary"
                                    v-bind="props"
                                    :disabled="egidLoading || !addressComplete || readOnlyCalc"
                                    rounded
                                    small
                                    class="mb-1"
                                    @click="egidLookup"
                                >
                                    {{ $t('egid_assign') }}
                                </v-btn>
                            </template>
                            <span>{{ $t('egid_find_and_assign') }}</span>
                        </v-tooltip>
                    </template>
                </KpEgidfield>
            </v-col>
            <v-col cols="12" sm="6">
                <v-checkbox
                    class="mt-6"
                    :model-value="liegenschaft.ist_neubau"
                    @update:model-value="(value) => liegenschaft.set('ist_neubau', value)"
                    :readonly="readOnlyCalc"
                    :label="$t('neubau')"
                    :infotext="infotext('ist_neubau')"
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" sm="6">
                <KpNumberfield
                    :model-value="liegenschaft.baujahr"
                    :label="$t('baujahr') + (liegenschaft.ist_neubau ? '' : ' *')"
                    :readonly="readOnlyCalc"
                    :rules="
                        liegenschaft.ist_neubau ? [] : [rules.between(1000, 2100), rules.required]
                    "
                    type="number"
                    @update:model-value="(v: number | null) => liegenschaft.set('baujahr', v)"
                    :infotext="infotext('baujahr')"
                />
            </v-col>
            <v-col cols="12" sm="6">
                <v-checkbox
                    class="mt-6"
                    :model-value="liegenschaft.baujahr_ist_geschaetzt"
                    @update:model-value="
                        (value) => liegenschaft.set('baujahr_ist_geschaetzt', value)
                    "
                    :label="$t('baujahr_geschaetzt')"
                    :readonly="readOnlyCalc"
                    :infotext="infotext('baujahr_ist_geschaetzt')"
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" sm="6">
                <kp-textfield
                    :model-value="liegenschaft.parzellen_nummer"
                    @update:model-value="(value) => liegenschaft.set('parzellen_nummer', value)"
                    :readonly="readOnlyCalc"
                    :label="$t('parz_nr')"
                    :infotext="infotext('parzellen_nummer')"
                />
            </v-col>
            <v-col cols="12" sm="6">
                <kp-textfield
                    :model-value="liegenschaft.assekuranz_nummer"
                    @update:model-value="(value) => liegenschaft.set('assekuranz_nummer', value)"
                    :readonly="readOnlyCalc"
                    :label="$t('assek_nr')"
                    :infotext="infotext('assekuranz_nummer')"
                />
            </v-col>
        </v-row>
        <ApplyDlg v-model="applyDlg" :changes="gwrdata" @apply="applyGwrData" />
    </v-container>
</template>
